import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import CheckDialog from "../components/checks/check-dialog.tsx";
import { SuggestedChecksTab } from "../components/checks/suggested-checks-tab.tsx";
import { DataGrid } from "../components/data-grid";
import { PageHeader } from "../components/page-header";
import { RunStatusChip } from "../components/run-status-chip.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import type { Check, CheckSchema, SuggestedChecksResponse } from "../types.ts";
import { PfsScoreChip } from "../utils/pfs-utils.tsx";

interface CheckActionColumnProps extends GridRenderCellParams<Check> {
  onDelete: () => void;
}

const CheckActionColumn = ({ row, onDelete }: CheckActionColumnProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { mutate: runCheck, isPending: isRunningCheck } = useApiMutation<void, { run_id: string }>(
    "POST",
    `/checks/${row.id}/run`,
    {
      onSuccess: () => {
        enqueueSnackbar(
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body1">Check run started!</Typography>
            <IconButton
              aria-label="View check"
              onClick={() => {
                nav(`/checks/${row.id}`);
              }}
              size="small"
              color="inherit"
            >
              <OpenInNewIcon htmlColor="#fff" />
            </IconButton>
          </Stack>,
          {
            variant: "success",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          },
        );
      },
    },
  );

  const { mutate: deleteCheck, isPending: isDeletingCheck } = useApiMutation<void, void>(
    "DELETE",
    `/checks/${row.id}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Check deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        onDelete();
        setDeleteConfirmOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting check: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
      },
    },
  );

  const handleDeleteConfirm = () => {
    deleteCheck({});
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <Box alignItems="center" display="flex">
        <Tooltip title="Run check">
          <IconButton
            size="small"
            color="default"
            disabled={isRunningCheck}
            onClick={() => {
              runCheck({});
            }}
            aria-label="Run check"
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete check">
          <IconButton
            size="small"
            color="error"
            disabled={isDeletingCheck}
            onClick={() => setDeleteConfirmOpen(true)}
            aria-label="Delete check"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Check Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the check "{row.name}"? This action will soft delete the
            check and it will no longer be visible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={isDeletingCheck}>
            {isDeletingCheck ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

type ChecksTab = "all" | "suggested";

export default function ChecksPage() {
  const [newCheck, setNewCheck] = useState<CheckSchema | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Read tab from URL params, default to "all"
  const urlTab = searchParams.get("tab") as ChecksTab | null;
  const activeTab: ChecksTab = urlTab === "suggested" ? "suggested" : "all";

  const { enqueueSnackbar } = useSnackbar();

  // Fetch suggested checks count for badge
  const { data: suggestedData } = useApiQuery<SuggestedChecksResponse>(
    "/checks/suggested?status=pending&limit=1",
  );
  const suggestedCount = suggestedData?.total ?? 0;

  const {
    data: checksResponse,
    isLoading: isLoadingChecks,
    refetch: refetchChecks,
  } = useApiQuery<ChecksListResponse>(
    searchQuery ? `/checks?q=${encodeURIComponent(searchQuery)}` : "/checks",
  );

  const checks = checksResponse?.checks ?? [];

  const { mutate: createCheck, isPending: isCreatingCheck } = useApiMutation<
    CheckSchema,
    { id: string }
  >("POST", "/checks", {
    onSuccess: () => {
      enqueueSnackbar("Check created!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      refetchChecks();
      setNewCheck(null);
    },
  });

  const handleDeleteCheck = () => {
    refetchChecks();
  };

  const handleRowClick = (params: GridRowParams<Check>) => {
    nav(`/checks/${params.row.id}`);
  };

  const checkColumns: GridColDef<Check>[] = [
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "check_type",
      headerName: "Type",
      type: "string",
      width: 100,
      valueGetter: (val: Check["check_type"]) => {
        return val?.toUpperCase() ?? "API";
      },
    },
    {
      field: "run_status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        if (!params.value) return "-";
        return <RunStatusChip status={params.value} />;
      },
    },
    {
      field: "pfs_score",
      headerName: "PFS",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (params.value == null) return "-";
        return <PfsScoreChip score={params.value} />;
      },
      description: "Probabilistic Flakiness Score",
    },
    {
      field: "created_at",
      type: "dateTime",
      headerName: "Created At",
      valueGetter: (val: Check["created_at"]) => {
        return new Date(val);
      },
      minWidth: 180,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => <CheckActionColumn {...params} onDelete={handleDeleteCheck} />,
    },
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: ChecksTab) => {
    if (newValue === "all") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", newValue);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <Box data-testid="checks-page">
      <PageHeader
        title="Checks"
        subtitle="Manage your API health checks"
        sx={{ borderRadius: 3 }}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {activeTab === "all" && (
              <>
                <TextField
                  size="small"
                  placeholder="Search checks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "grey.500" }} />,
                  }}
                  sx={{
                    minWidth: 300,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "common.white",
                    },
                  }}
                />
                <Tooltip title="New Check" placement="top">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setNewCheck({
                        name: "",
                        app_config_id: "",
                        check_type: "api",
                        config: {
                          url: "",
                          method: "GET",
                          follow_redirects: true,
                        },
                        retry_strategy: "exponential",
                        max_retries: 3,
                        retry_delay_seconds: 1,
                        backoff_multiplier: 2,
                        timeout_ms: 30000,
                      })
                    }
                    sx={{
                      borderRadius: 2,
                      p: 1.5,
                      border: "0px solid",
                      borderColor: "divider",
                      color: "grey.800",
                      backgroundColor: "transparent",
                      "&:hover": {
                        borderColor: "info.dark",
                        backgroundColor: "common.white",
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="All Checks" value="all" />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <LightbulbIcon fontSize="small" />
                <span>Suggested</span>
                {suggestedCount > 0 && (
                  <Chip
                    label={suggestedCount > 99 ? "99+" : suggestedCount}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.75rem", ml: 0.5 }}
                  />
                )}
              </Stack>
            }
            value="suggested"
          />
        </Tabs>
      </Box>

      {!!newCheck && (
        <CheckDialog
          open={!!newCheck}
          mode="create"
          onClose={() => setNewCheck(null)}
          onSave={(data) => createCheck(data)}
          data={newCheck}
          setData={(data) => setNewCheck(data as CheckSchema)}
          isProcessing={isCreatingCheck}
        />
      )}

      {activeTab === "all" && (
        <DataGrid
          loading={isLoadingChecks}
          rows={checks}
          columns={checkColumns}
          pageSizeOptions={[25]}
          minHeight={`calc(100vh - 250px)`}
          onRowClick={handleRowClick}
        />
      )}

      {activeTab === "suggested" && <SuggestedChecksTab />}
    </Box>
  );
}
